import { MULTI_ANALYSIS_MEASURE_TYPE } from "../types";

export const TEMPLATE_FILTER_NULL_VALUE = "";

export const TEMPLATE_FILTER_UNDEFINED_ENUM_VALUE = "255";

export const MEASUREMENT_RANGE_TYPE_OPTIONS = ["1", "2", "3", "255"] as const;

export const UNITS_PRESENTATION_OPTIONS = ["0", "1", "2", "255"] as const;

export const COMMON_UNIT_OPTIONS = ["0", "1", "2", "5", "6", "128", "129", "130", "132", "133", "134", "255"] as const;

const excludeUndefinedEnumOption = <T extends string>(options: readonly T[]): T[] =>
  options.filter(option => option !== TEMPLATE_FILTER_UNDEFINED_ENUM_VALUE);

export const MANUAL_TEMPLATE_RANGE_TYPE_OPTIONS = excludeUndefinedEnumOption(MEASUREMENT_RANGE_TYPE_OPTIONS);

export const MANUAL_TEMPLATE_PRESENTATION_OPTIONS = excludeUndefinedEnumOption(UNITS_PRESENTATION_OPTIONS);

export const MANUAL_TEMPLATE_UNIT_OPTIONS = excludeUndefinedEnumOption(COMMON_UNIT_OPTIONS);

export interface ITemplateCellFilterValues {
  rangeType: string;
  units: string;
  presentation: string;
  param1: string;
  param2: string;
  param3: string;
}

export const EMPTY_TEMPLATE_CELL_FILTER_VALUES: ITemplateCellFilterValues = {
  rangeType: TEMPLATE_FILTER_NULL_VALUE,
  units: TEMPLATE_FILTER_NULL_VALUE,
  presentation: TEMPLATE_FILTER_NULL_VALUE,
  param1: TEMPLATE_FILTER_NULL_VALUE,
  param2: TEMPLATE_FILTER_NULL_VALUE,
  param3: TEMPLATE_FILTER_NULL_VALUE,
};

const toFilterString = (value: unknown): string => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return TEMPLATE_FILTER_NULL_VALUE;
  }

  return String(value);
};

export const readTemplateCellFilterValues = (filters: Record<string, unknown>): ITemplateCellFilterValues => ({
  rangeType: toFilterString(filters.rangeType),
  units: toFilterString(filters.units),
  presentation: toFilterString(filters.presentation),
  param1: toFilterString(filters.param1),
  param2: toFilterString(filters.param2),
  param3: toFilterString(filters.param3),
});

export const normalizeManualTemplateSelectValue = (value: string): string =>
  value === TEMPLATE_FILTER_UNDEFINED_ENUM_VALUE ? TEMPLATE_FILTER_NULL_VALUE : value;

const toNullableTemplateEnumFilter = (value: string): string | null =>
  normalizeManualTemplateSelectValue(value) || null;

export const FREQUENCY_RANGE_DECIMAL_PLACES = 2;

export const parseFrequencyRangeParam = (value: string): number | null => {
  const trimmed = value.trim().replace(",", ".");

  if (!trimmed) {
    return null;
  }

  const numeric = Number(trimmed);

  return Number.isFinite(numeric) ? numeric : null;
};

export const formatFrequencyRangeParam = (value: number): string =>
  parseFloat(value.toFixed(FREQUENCY_RANGE_DECIMAL_PLACES)).toString();

export const normalizeFrequencyRangeParamInput = (value: string): string => {
  const parsed = parseFrequencyRangeParam(value);

  if (parsed === null) {
    return value.trim() === "" ? TEMPLATE_FILTER_NULL_VALUE : value.trim();
  }

  return formatFrequencyRangeParam(parsed);
};

const toNullableFrequencyRangeParam = (value: string): number | null => {
  const parsed = parseFrequencyRangeParam(value);

  if (parsed === null) {
    return null;
  }

  return parseFloat(parsed.toFixed(FREQUENCY_RANGE_DECIMAL_PLACES));
};

export const buildTemplateCellFilters = (
  values: ITemplateCellFilterValues,
  selectedMeasureNames: readonly string[] = [],
): Record<string, unknown> => {
  const filters: Record<string, unknown> = {
    rangeType: toNullableTemplateEnumFilter(values.rangeType),
    units: toNullableTemplateEnumFilter(values.units),
    presentation: toNullableTemplateEnumFilter(values.presentation),
    param1: toNullableFrequencyRangeParam(values.param1),
    param2: toNullableFrequencyRangeParam(values.param2),
    param3: values.param3 ? Number(values.param3) : null,
  };

  if (selectedMeasureNames.length === 1) {
    filters.Name = selectedMeasureNames[0];
  } else if (selectedMeasureNames.length > 1) {
    filters.Name = [...selectedMeasureNames];
  }

  return filters;
};

const hasFilterValue = (value: unknown): boolean =>
  Array.isArray(value)
    ? value.some(item => item !== null && item !== undefined && String(item).trim() !== "")
    : value !== null && value !== undefined && String(value).trim() !== "";

const hasTemplateFrequencyRangeFilter = (filters: Record<string, unknown>): boolean =>
  hasFilterValue(filters.param1) || hasFilterValue(filters.param2);

export const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return null;
  }

  const numeric = Number(value);

  return Number.isFinite(numeric) ? numeric : null;
};

export const hasMeasureFrequencyBand = (measureParam1: unknown, measureParam2: unknown): boolean =>
  toFiniteNumber(measureParam1) !== null && toFiniteNumber(measureParam2) !== null;

const readTemplateFrequencyRangeFilter = (
  filters: Record<string, unknown>,
): { param1: number | null; param2: number | null } => ({
  param1: toFiniteNumber(filters.param1),
  param2: toFiniteNumber(filters.param2),
});

export const measureBandMatchesFrequencyFilter = (
  measureParam1: unknown,
  measureParam2: unknown,
  filters: Record<string, unknown>,
): boolean => {
  if (!hasTemplateFrequencyRangeFilter(filters)) {
    return true;
  }

  const measureStart = toFiniteNumber(measureParam1);
  const measureEnd = toFiniteNumber(measureParam2);

  if (measureStart === null || measureEnd === null) {
    return true;
  }

  const { param1: filterStart, param2: filterEnd } = readTemplateFrequencyRangeFilter(filters);

  if (filterStart !== null && measureStart < filterStart) {
    return false;
  }

  if (filterEnd !== null && measureEnd > filterEnd) {
    return false;
  }

  return true;
};

export const getSelectedMeasureNamesFromFilters = (filters: Record<string, unknown>): string[] => {
  const rawName = filters.Name;

  if (Array.isArray(rawName)) {
    return rawName.map(String).filter(Boolean);
  }

  if (typeof rawName === "string" && rawName.trim()) {
    return [rawName];
  }

  return [];
};

export const supportsFrequencyRangeParams = (measureType: MULTI_ANALYSIS_MEASURE_TYPE): boolean =>
  measureType === MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM || measureType === MULTI_ANALYSIS_MEASURE_TYPE.SIGNAL_FORM;

export const supportsOrbitParams = (measureType: MULTI_ANALYSIS_MEASURE_TYPE): boolean =>
  measureType === MULTI_ANALYSIS_MEASURE_TYPE.ORBIT;

export const isFrequencyRangeBoundsInvalid = (param1: string, param2: string): boolean => {
  const start = parseFrequencyRangeParam(param1);
  const end = parseFrequencyRangeParam(param2);

  if (start === null || end === null) {
    return false;
  }

  return start >= end;
};

export const draftTemplateHasInvalidFrequencyRanges = (
  cells: readonly { measureType: MULTI_ANALYSIS_MEASURE_TYPE; filters: Record<string, unknown> }[],
): boolean =>
  cells.some(cell => {
    if (!supportsFrequencyRangeParams(cell.measureType)) {
      return false;
    }

    const { param1, param2 } = readTemplateCellFilterValues(cell.filters);

    return isFrequencyRangeBoundsInvalid(param1, param2);
  });
